import { useEffect, useState } from "react";
import axios from "axios";
import { api, API_BASE, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { formatDateTime, formatINR } from "../lib/format";
import { Receipt } from "lucide-react";

const STATUS_STYLE = {
    approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
    pending: "bg-amber-50 text-amber-800 border-amber-200",
    rejected: "bg-rose-50 text-rose-800 border-rose-200",
    reversed: "bg-rose-50 text-rose-800 border-rose-200",
    archived: "bg-slate-100 text-slate-700 border-slate-200",
};

function Row({ label, value, testid }) {
    if (value === undefined || value === null || value === "") return null;
    return (
        <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100 last:border-0 text-sm">
            <span className="text-slate-500 shrink-0">{label}</span>
            <span className="text-slate-900 font-medium text-right break-words" data-testid={testid}>{value}</span>
        </div>
    );
}

function who(name, user, role) {
    const base = name || user || "—";
    const extra = [user && user !== name ? user : null, role].filter(Boolean).join(" · ");
    return extra ? `${base} (${extra})` : base;
}

/** Shows full audit detail for a collection entry.
 *  Pass `paymentId` (staff) or `paymentId` + `portalToken` (customer), or a ready `entry` object. */
export function EntryDetailDialog({ paymentId, portalToken, entry, open, onClose }) {
    const [d, setD] = useState(entry || null);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (!open) return;
        setErr("");
        if (entry) { setD(entry); return; }
        if (!paymentId) return;
        setD(null);
        const req = portalToken
            ? axios.get(`${API_BASE}/portal/payments/${paymentId}/detail`, { params: { t: portalToken } })
            : api.get(`/payments/${paymentId}/detail`);
        req.then((r) => setD(r.data)).catch((e) => setErr(formatApiError(e)));
    }, [open, paymentId, portalToken, entry]);

    const status = d?.current_status || d?.approval_status || "approved";
    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
            <DialogContent className="rounded-2xl max-w-md" data-testid="entry-detail-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Receipt className="h-5 w-5 text-blue-900" /> Collection Entry
                    </DialogTitle>
                </DialogHeader>
                {err && <p className="text-sm text-rose-700" data-testid="entry-detail-error">{err}</p>}
                {!d && !err && <p className="text-sm text-slate-400">Loading…</p>}
                {d && (
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="num text-2xl font-bold text-slate-900" data-testid="entry-amount">{formatINR(d.amount)}</div>
                            <span className={`text-[10px] uppercase tracking-wide font-semibold border rounded-md px-2 py-0.5 ${STATUS_STYLE[status] || STATUS_STYLE.archived}`}
                                data-testid="entry-current-status">{status}</span>
                        </div>
                        <div>
                            <Row label="Collection date / time" value={formatDateTime(d.collection_datetime)} testid="entry-collection-datetime" />
                            {d.backdated && <Row label="Entry type" value="Backdated (Admin)" testid="entry-backdated" />}
                            <Row label="Collected by" value={d.collected_by || d.collected_by_name ? who(d.collected_by_name, d.collected_by, d.collected_by_role) : null} testid="entry-collected-by" />
                            <Row label="Entry created date / time" value={formatDateTime(d.entry_created_at)} testid="entry-created-at" />
                            <Row label="Entry recorded by" value={who(d.entry_recorded_by_name || d.entered_by_name, d.entry_recorded_by || d.entered_by, d.entry_recorded_by_role || d.entered_by_role)} testid="entry-entered-by" />
                            <Row label="Approval status" value={d.approval_status} testid="entry-approval-status" />
                            <Row label={d.approval_status === "rejected" ? "Rejected date / time" : "Approved date / time"}
                                value={formatDateTime(d.approved_at || d.rejected_at)} testid="entry-approved-at" />
                            <Row label={d.approval_status === "rejected" ? "Rejected by" : "Approved by"}
                                value={who(null, d.approved_by || d.rejected_by, d.approved_by_role)} testid="entry-approved-by" />
                            <Row label="Product" value={[d.product, d.reference].filter(Boolean).join(" · ")} testid="entry-product" />
                            <Row label="Customer" value={[d.customer_name, d.customer_code].filter(Boolean).join(" · ")} />
                            {d.product === "Loan" && (
                                <>
                                    <Row label="Interest" value={formatINR(d.interest_paid || 0)} testid="entry-interest" />
                                    <Row label="Principal" value={formatINR(d.principal_paid || 0)} testid="entry-principal" />
                                    {d.interest_accrued != null && <Row label="Interest accrued (daily)" value={formatINR(d.interest_accrued)} testid="entry-interest-accrued" />}
                                    {d.interest_carried > 0 && <Row label="Unpaid interest carried" value={formatINR(d.interest_carried)} testid="entry-interest-carried" />}
                                    {d.split_adjustments?.length > 0 && <Row label="Re-allocated"
                                        value={d.split_adjustments.map((a) => `${formatDateTime(a.at)} · ${a.by}`).join("; ")} testid="entry-reallocated" />}
                                    <Row label="Balance after" value={formatINR(d.balance_after || 0)} />
                                </>
                            )}
                            {d.product === "Saving Scheme" && <Row label="Balance after" value={formatINR(d.balance_after || 0)} />}
                            {d.product === "Investment" && (
                                <>
                                    <Row label="Investment date" value={d.start_date} testid="entry-inv-start" />
                                    <Row label="Interest rate" value={d.interest_rate != null ? `${d.interest_rate}% p.a.` : null} />
                                    <Row label="Maturity date" value={d.maturity_date} testid="entry-inv-maturity-date" />
                                    <Row label="Maturity amount" value={d.maturity_amount != null ? formatINR(d.maturity_amount) : null} testid="entry-inv-maturity-amount" />
                                </>
                            )}
                            <Row label="Payment mode" value={(d.mode || "").toUpperCase()} testid="entry-mode" />
                            <Row label="Reference / Receipt" value={d.receipt_no} />
                            <Row label="Notes" value={d.notes} />
                            {d.reversed_at && <Row label="Reversed" value={`${formatDateTime(d.reversed_at)} · ${d.reversed_by || ""} · ${d.reversal_reason || ""}`} />}
                            {d.archived_at && <Row label="Archived" value={`${formatDateTime(d.archived_at)} · ${d.archived_by || ""} · ${d.archive_reason || ""}`} />}
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
